import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Cloud, CloudRain, CloudSnow, Sun, CloudSun, Wind, Droplet, Calendar } from "lucide-react";

/**
 * WeatherIntel — practical weather intelligence for portfolio managers.
 *
 * Pulls current conditions + 3-day forecast for our two markets (NYC + Charlotte)
 * from wttr.in (no API key, no auth). Beyond just displaying weather, it computes
 * the "best cleaning day" — the day this week with lowest rain chance + moderate
 * temp, which is genuinely useful info for maintenance scheduling.
 *
 * Reinforces the brand story: we understand your ops calendar. We plan around
 * the weather, so you don't have to.
 */
const MARKETS = [
  { key: "nyc",       label: "NYC",           city: "New+York" },
  { key: "charlotte", label: "Charlotte, NC", city: "Charlotte" },
];

function iconFor(code) {
  const c = Number(code || 0);
  // Simplified mapping from WWO codes
  if (c === 113) return Sun;
  if ([116, 119, 122].includes(c)) return CloudSun;
  if (c >= 176 && c <= 302) return CloudRain;
  if (c >= 305 && c <= 356) return CloudRain;
  if (c >= 179 && c <= 227) return CloudSnow;
  if (c >= 227 && c <= 395) return CloudSnow;
  return Cloud;
}

function pickBestDay(forecast) {
  if (!forecast?.length) return null;
  let best = { idx: 0, score: -Infinity };
  forecast.forEach((d, i) => {
    const rainMm = parseFloat(d.totalprecip_mm || d.hourly?.[4]?.precipMM || "0");
    const temp = parseFloat(d.avgtempF || d.hourly?.[4]?.tempF || "60");
    // Lower rain + moderate temp (55..80°F) = higher score
    const tempComfort = 1 - Math.abs(temp - 68) / 40;
    const score = -rainMm * 3 + tempComfort * 2;
    if (score > best.score) best = { idx: i, score };
  });
  return best.idx;
}

export default function WeatherIntel() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const results = {};
      await Promise.all(
        MARKETS.map(async (m) => {
          try {
            const res = await fetch(`https://wttr.in/${m.city}?format=j1`);
            const json = await res.json();
            results[m.key] = json;
          } catch (e) {
            results[m.key] = null;
          }
        })
      );
      setData(results);
      setLoading(false);
    })();
  }, []);

  return (
    <section
      className="py-24 md:py-32 relative"
      style={{ background: "var(--bg-2)" }}
      data-testid="weather-intel"
    >
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(1000px 500px at 50% 0%, rgba(198,168,124,0.06), transparent 60%)",
      }} />
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
          className="grid md:grid-cols-12 gap-8 mb-14"
        >
          <div className="md:col-span-3 overline pt-2">Weather intelligence</div>
          <div className="md:col-span-9">
            <h2 className="font-display text-5xl md:text-7xl" style={{ letterSpacing: "-0.035em", lineHeight: 0.95 }}>
              We schedule against the sky,{" "}
              <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>so you don't have to.</span>
            </h2>
            <p className="mt-6 font-body max-w-2xl" style={{ color: "var(--text-2)", fontSize: 17, lineHeight: 1.65 }}>
              Live conditions across both markets. We use the same forecast to plan every soft-wash and DI-water pass — so your finish holds.
            </p>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {MARKETS.map((m) => (
            <MarketCard key={m.key} market={m} raw={data[m.key]} loading={loading} />
          ))}
        </div>
      </div>
    </section>
  );
}

function MarketCard({ market, raw, loading }) {
  const current = raw?.current_condition?.[0];
  const forecast = raw?.weather || [];
  const bestIdx = pickBestDay(forecast);

  const tempF = current?.temp_F;
  const feels = current?.FeelsLikeF;
  const wind = current?.windspeedMiles;
  const humidity = current?.humidity;
  const desc = current?.weatherDesc?.[0]?.value;
  const code = current?.weatherCode;
  const Icon = iconFor(code);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="glass-card glass-reflect float-lift p-8 md:p-10 relative overflow-hidden"
      data-testid={`weather-card-${market.key}`}
    >
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--gold)" }}>
            Market · {market.label}
          </div>
          <div className="mt-3 flex items-end gap-4">
            <div className="font-display leading-none" style={{ fontSize: "clamp(3.4rem, 5.4vw, 5rem)", color: "var(--text)", letterSpacing: "-0.04em" }}>
              {loading ? "—" : tempF ? `${tempF}°` : "—"}
            </div>
            <div className="pb-3">
              <div className="font-body text-lg" style={{ color: "var(--text-2)" }}>{desc || (loading ? "Loading…" : "—")}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] mt-1" style={{ color: "var(--text-3)" }}>
                Feels {feels || "—"}° · Humidity {humidity || "—"}% · Wind {wind || "—"} mph
              </div>
            </div>
          </div>
        </div>
        <div className="w-16 h-16 flex items-center justify-center" style={{
          borderRadius: "999px",
          background: "rgba(198,168,124,0.08)",
          border: "1px solid var(--line-gold)",
        }}>
          <Icon size={26} strokeWidth={1.4} style={{ color: "var(--gold-2)" }} />
        </div>
      </div>

      <div className="hairline my-6" />

      {/* 3-day forecast */}
      <div className="grid grid-cols-3 gap-3">
        {forecast.slice(0, 3).map((day, i) => {
          const DayIcon = iconFor(day.hourly?.[4]?.weatherCode);
          const best = i === bestIdx;
          const dt = day.date ? new Date(day.date) : null;
          const dayName = dt ? dt.toLocaleDateString("en-US", { weekday: "short" }) : `Day ${i + 1}`;
          return (
            <div
              key={i}
              className="p-4 relative overflow-hidden"
              style={{
                background: best ? "rgba(198,168,124,0.14)" : "rgba(255,255,255,0.03)",
                border: best ? "1px solid var(--gold)" : "1px solid rgba(255,255,255,0.06)",
                borderRadius: "14px",
              }}
              data-testid={`weather-day-${market.key}-${i}`}
            >
              {best && (
                <div className="absolute top-2 right-2 font-mono text-[8px] uppercase tracking-[0.28em] px-1.5 py-0.5" style={{
                  background: "var(--gold)", color: "#0a0a0d", borderRadius: "999px",
                }}>
                  Ideal
                </div>
              )}
              <div className="font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: best ? "var(--gold-2)" : "var(--text-3)" }}>
                {dayName}
              </div>
              <div className="mt-3 flex items-center gap-3">
                <DayIcon size={16} strokeWidth={1.5} style={{ color: best ? "var(--gold-2)" : "var(--text-2)" }} />
                <div className="font-display text-2xl" style={{ letterSpacing: "-0.02em", color: "var(--text)" }}>
                  {day.maxtempF || "—"}°
                  <span className="ml-1 font-body text-sm" style={{ color: "var(--text-3)" }}>/ {day.mintempF || "—"}°</span>
                </div>
              </div>
              <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.28em]" style={{ color: "var(--text-3)" }}>
                <Droplet size={9} className="inline mr-1" />
                {day.hourly?.[4]?.chanceofrain || "0"}% rain
              </div>
            </div>
          );
        })}
      </div>

      {bestIdx !== null && forecast[bestIdx] && (
        <div className="mt-6 p-4 flex items-start gap-3" style={{
          background: "linear-gradient(135deg, rgba(198,168,124,0.12), rgba(198,168,124,0.04))",
          border: "1px solid var(--line-gold)",
          borderRadius: "14px",
        }}>
          <Calendar size={14} style={{ color: "var(--gold-2)", marginTop: 2 }} />
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.32em]" style={{ color: "var(--gold-2)" }}>
              Best cleaning day this week
            </div>
            <div className="font-body mt-1" style={{ color: "var(--text)", fontSize: 14 }}>
              {(() => {
                const d = forecast[bestIdx];
                const dt = d.date ? new Date(d.date) : null;
                const name = dt ? dt.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }) : "—";
                return `${name} · ${d.maxtempF}°F high · ${d.hourly?.[4]?.chanceofrain || "0"}% rain chance`;
              })()}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
